import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { invoicesAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';

// Customer Storage Fees page. Shows a live snapshot of what the
// customer is currently paying for monthly storage: four KPI cards
// at the top, a warehouse / storage-class breakdown, a per-pallet
// detail table, the customer's configured rate schedule, and a
// short history of finalized monthly storage invoices.
//
// All data comes from `/billing/storage/current` and
// `/billing/storage/history`. The backend scopes both to the
// authenticated user's company_id so no additional filtering is
// needed client-side.

const formatMoney = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value));
};

const formatDate = (value) => {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatShortDate = (value) => {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const humanize = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const KpiCard = ({ label, value, sublabel, accent = 'navy' }) => {
  const accentClass = accent === 'gold'
    ? 'border-l-4 border-brand-gold'
    : 'border-l-4 border-brand-navy';
  return (
    <div className={`bg-white shadow rounded-lg p-5 ${accentClass}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-brand-navy">{value}</div>
      {sublabel && <div className="mt-1 text-xs text-gray-500">{sublabel}</div>}
    </div>
  );
};

const StorageFees = () => {
  const { data: snapshot, isLoading, isError, error } = useQuery(
    ['customer-storage-snapshot'],
    () => invoicesAPI.getStorageSnapshot(),
    { refetchOnWindowFocus: false, staleTime: 60 * 1000 }
  );

  const { data: historyData } = useQuery(
    ['customer-storage-history'],
    () => invoicesAPI.getStorageHistory(6),
    { refetchOnWindowFocus: false, staleTime: 60 * 1000 }
  );

  const lastMonthBilled = useMemo(() => {
    const list = historyData?.history || [];
    if (!list.length) return null;
    return list[0];
  }, [historyData]);

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorAlert message={error?.message || 'Failed to load storage fees'} />;

  const totals = snapshot?.totals || {};
  const byWarehouse = snapshot?.byWarehouse || [];
  const rateSchedule = snapshot?.rateSchedule || [];
  const units = snapshot?.units || [];
  const isFlatRate = snapshot?.flatRate === true;
  const agreement = snapshot?.agreement || null;

  // "Last Month Billed" prefers the most recent invoice from history;
  // falls back to an em-dash if this is the customer's first month.
  const lastMonthValue = lastMonthBilled ? formatMoney(lastMonthBilled.totalAmount) : '--';
  const lastMonthSub = lastMonthBilled
    ? `${lastMonthBilled.invoiceNumber || 'Invoice'} · ${formatDate(lastMonthBilled.issueDate)}`
    : 'No prior storage invoice';

  const nextInvoiceLabel = snapshot?.nextInvoiceDate
    ? formatDate(snapshot.nextInvoiceDate)
    : '--';

  const daysElapsed = snapshot?.daysElapsedInMonth || 0;
  const daysInMonth = snapshot?.daysInMonth || 30;
  const monthProgress = Math.round((daysElapsed / daysInMonth) * 100);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-brand-navy">Storage Fees</h1>
        <p className="mt-1 text-sm text-gray-500">
          Live snapshot of your monthly storage charges. Bills are finalized
          and invoiced on the 1st of each month.
        </p>
      </div>

      {/* Flat-rate agreement banner (only shown when the customer
          has an active StorageAgreement). Replaces the per-pallet
          rate schedule / warehouse breakdown sections below. */}
      {isFlatRate && agreement && (
        <div className="rounded-lg border border-brand-gold bg-brand-gold/5 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Flat-Rate Storage Agreement</div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <div>
              <span className="text-3xl font-bold text-brand-navy">{formatMoney(agreement.monthlyAmount)}</span>
              <span className="text-sm text-gray-600"> / month</span>
            </div>
            <div className="text-sm text-gray-700">
              {Number(agreement.sqft).toLocaleString()} sqft rented
            </div>
            <div className="text-xs text-gray-500">
              Active since {formatDate(agreement.startDate)}
            </div>
          </div>
          {agreement.notes && (
            <p className="mt-2 text-sm text-gray-600 italic">{agreement.notes}</p>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Per-pallet rates do not apply while this agreement is active. The detail table below
            shows your actual inventory in storage for reference only -- you are billed the flat
            monthly amount regardless of pallet count.
          </p>
        </div>
      )}

      {/* KPI row -- 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label={isFlatRate ? 'Monthly Flat Rate' : 'Current Month Projected'}
          value={formatMoney(isFlatRate ? agreement?.monthlyAmount : totals.projectedMonthCharge)}
          sublabel={isFlatRate
            ? `Flat rate for ${Number(agreement?.sqft || 0).toLocaleString()} sqft`
            : `${formatMoney(totals.monthToDateCharge)} charged so far (${monthProgress}% of month)`}
          accent="gold"
        />
        <KpiCard
          label="Last Month Billed"
          value={lastMonthValue}
          sublabel={lastMonthSub}
        />
        <KpiCard
          label="Pallets in Storage"
          value={(totals.palletEquivalents || 0).toFixed(1)}
          sublabel={`${totals.unitsInStorage || 0} storage unit${totals.unitsInStorage === 1 ? '' : 's'}`}
        />
        <KpiCard
          label="Next Invoice Date"
          value={nextInvoiceLabel}
          sublabel="Monthly storage bill generates automatically on the 1st"
        />
      </div>

      {/* Warehouse / storage-class breakdown */}
      {!isFlatRate && byWarehouse.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-brand-navy">By Warehouse</h2>
            <p className="text-sm text-gray-500">
              Your storage footprint broken down by warehouse and storage class.
            </p>
          </div>
          <div className="divide-y divide-gray-100">
            {byWarehouse.map((wh) => (
              <div key={wh.warehouseId} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-brand-navy">
                      {wh.warehouseName || 'Warehouse'}
                      {wh.warehouseCode && <span className="ml-2 text-xs text-gray-500">({wh.warehouseCode})</span>}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {wh.totalPalletEquivalent.toFixed(1)} pallet-equivalents across {wh.totalUnits} unit{wh.totalUnits === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-brand-navy">{formatMoney(wh.projectedCharge)}</div>
                    <div className="text-xs text-gray-500">projected this month</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {wh.byStorageClass.map((sc) => (
                    <div key={sc.storageClass} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <div className="text-xs font-medium text-gray-600">{humanize(sc.storageClass)}</div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm font-semibold text-brand-navy">{sc.palletEquivalent.toFixed(1)} pallets</span>
                        <span className="text-xs text-gray-500">{formatMoney(sc.projectedCharge)}</span>
                      </div>
                      {sc.ratePerMonth != null && (
                        <div className="text-xs text-gray-500">{formatMoney(sc.ratePerMonth)} / pallet / mo</div>
                      )}
                      {sc.cartonRoundingAdjustment > 0 && (
                        <div
                          className="text-xs text-gray-400"
                          title="Loose cartons share pallet positions; partial positions round up to the next whole pallet"
                        >
                          incl. {formatMoney(sc.cartonRoundingAdjustment)} carton rounding
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-unit detail table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-brand-navy">Storage Detail</h2>
          <p className="text-sm text-gray-500">
            {isFlatRate
              ? 'Your current inventory in storage. Shown for reference only -- under a flat-rate agreement the monthly amount is fixed regardless of pallet count.'
              : 'Every storage unit with charges this month. Charges prorate per day; units that shipped or were consumed this month stop accruing on their departure date but keep the days already charged.'}
          </p>
        </div>
        {units.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">
            No inventory in storage this month. Submit an ASN to add inventory.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Received</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">SKU / Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Warehouse</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Storage Class</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Pallet-Eq.</th>
                  {!isFlatRate && (
                    <>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Days</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Daily Rate</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Month-to-Date</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Projected</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {units.map((u) => (
                  <tr key={u.id} className={u.stillInStorage === false ? 'bg-gray-50 text-gray-500' : ''}>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                      {formatShortDate(u.receivedDate)}
                      {u.stillInStorage === false && (
                        <span
                          className="ml-2 inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600"
                          title="This unit shipped or was consumed this month; it stopped accruing on its departure date but keeps the days already charged"
                        >
                          departed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {u.skus && u.skus.length > 0 ? (
                        <div>
                          <div className="font-medium">{u.skus[0].productSku}</div>
                          <div className="text-xs text-gray-500">{u.skus[0].productName}</div>
                          {u.skus.length > 1 && (
                            <div className="text-xs text-brand-gold">+{u.skus.length - 1} more SKU{u.skus.length > 2 ? 's' : ''}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Unknown</span>
                      )}
                      {u.requestNumber && (
                        <div className="text-xs text-gray-400 mt-0.5">from {u.requestNumber}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{u.warehouseName || '--'}</td>
                    <td className="px-4 py-3 text-gray-700">{humanize(u.storageClass)}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{Number(u.palletEquivalent || 0).toFixed(2)}</td>
                    {!isFlatRate && (
                      <>
                        <td className="px-4 py-3 text-right text-gray-700">
                          {u.daysElapsedThisMonth}
                          <span className="text-gray-400"> / {u.projectedDaysThisMonth}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">{formatMoney(u.dailyRate)}</td>
                        <td className="px-4 py-3 text-right text-gray-900 font-medium">{formatMoney(u.monthToDateCharge)}</td>
                        <td className="px-4 py-3 text-right text-brand-navy font-semibold">{formatMoney(u.projectedMonthCharge)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
              {!isFlatRate && (
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={7} className="px-4 py-3 text-right text-sm font-medium text-gray-600">Totals</td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{formatMoney(totals.monthToDateCharge)}</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-brand-navy">{formatMoney(totals.projectedMonthCharge)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Rate schedule panel */}
      {!isFlatRate && rateSchedule.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-brand-navy">Your Storage Rates</h2>
            <p className="text-sm text-gray-500">
              Configured rates per warehouse and storage class. Volume and
              long-term discounts apply automatically when thresholds are met.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Warehouse</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Storage Class</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Rate</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Volume Discount</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Long-term Discount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rateSchedule.map((r) => (
                  <tr key={r.id} className={r.isEnabled ? '' : 'opacity-50'}>
                    <td className="px-4 py-3 text-gray-900">{r.warehouseName || '--'}</td>
                    <td className="px-4 py-3 text-gray-900">{humanize(r.storageClass)}</td>
                    <td className="px-4 py-3 text-right text-gray-900 font-medium">
                      {formatMoney(r.feeAmount)}
                      <span className="text-xs text-gray-500 ml-1">/ {humanize(r.billingUnit)} / mo</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.volumeDiscountRate && r.volumeDiscountThreshold
                        ? `${formatMoney(r.volumeDiscountRate)} when ${r.volumeDiscountThreshold}+ pallets`
                        : <span className="text-gray-400">--</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.longtermDiscountRate && r.longtermDiscountMonths
                        ? `${formatMoney(r.longtermDiscountRate)} after ${r.longtermDiscountMonths} months`
                        : <span className="text-gray-400">--</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History */}
      {historyData?.history && historyData.history.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-brand-navy">Recent Monthly Bills</h2>
            <p className="text-sm text-gray-500">
              The last {historyData.history.length} storage invoice{historyData.history.length === 1 ? '' : 's'}.
            </p>
          </div>
          <ul className="divide-y divide-gray-100">
            {historyData.history.map((h) => (
              <li key={h.invoiceId} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-brand-navy">
                    {h.month && h.year
                      ? new Date(h.year, h.month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                      : h.invoiceNumber}
                  </div>
                  <div className="text-xs text-gray-500">
                    {h.invoiceNumber} · {formatDate(h.issueDate)} · {humanize(h.status)}
                  </div>
                </div>
                <div className="text-sm font-semibold text-brand-navy">{formatMoney(h.totalAmount)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StorageFees;
